import type { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionContext";
import { Spinner } from "./Spinner";

type Role = "super_admin" | "admin" | "manager" | "staff" | "cashier";

interface ProtectedRouteProps {
  /** Roles that are allowed to access this route. If omitted, any authenticated user is allowed. */
  requiredRoles?: Role[];
  /** Permission module to check via the dynamic permission matrix. */
  module?: string;
  children: ReactNode;
}

/**
 * AccessDenied — inline component rendered when the user is authenticated
 * but does not have the required role/permission for the current route.
 *
 * Requirements: 1.5, 4.2
 */
function AccessDenied() {
  return (
    <div
      role="alert"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
        <svg
          className="h-8 w-8 text-red-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          />
        </svg>
      </div>
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
        <p className="mt-1 text-sm text-gray-500">
          You don't have permission to view this page. Contact your
          administrator if you believe this is a mistake.
        </p>
      </div>
    </div>
  );
}

/**
 * ProtectedRoute — guards a route behind authentication and permission/role checks.
 *
 * Behaviour:
 *  - While auth state is loading: shows a full-screen spinner
 *  - If unauthenticated: redirects to /login, preserving the intended path in `state.from`
 *  - If `module` prop is provided: uses the dynamic permission matrix via `usePermissions`
 *  - If only `requiredRoles` provided: falls back to legacy role-based check
 *  - If permission check fails: renders <AccessDenied> inline
 *  - Otherwise: renders children
 *
 * Requirements: 1.1, 1.3, 1.5, 1.9, 4.1, 4.4, 4.5
 */
export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  requiredRoles,
  module,
  children,
}) => {
  const { user, role, isLoading } = useAuth();
  const { hasAccess, isLoading: permissionsLoading, permissions } = usePermissions();
  const location = useLocation();

  console.log("[ProtectedRoute] State:", {
    pathname: location.pathname,
    role,
    module,
    isLoading,
    permissionsLoading,
    permissions,
    hasAccess: module ? hasAccess(module) : undefined
  });

  // Still resolving auth state — show a neutral loading screen
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Spinner size="lg" className="text-primary-600" />
      </div>
    );
  }

  // Not authenticated — redirect to login, preserving the attempted URL
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If module prop is provided, use permission-based access check
  if (module) {
    // While permissions are loading, show spinner (don't deny access yet)
    if (permissionsLoading) {
      return (
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner size="lg" className="text-primary-600" />
        </div>
      );
    }

    if (!hasAccess(module)) {
      return <AccessDenied />;
    }
  }
  // Legacy: If requiredRoles provided, check role
  else if (requiredRoles && requiredRoles.length > 0) {
    if (!role || !requiredRoles.includes(role)) {
      return <AccessDenied />;
    }
  }

  return <>{children}</>;
};
